import {
    getPlatformLogo,
    isArivalPlatform,
    isGlobalBlockPlatform,
    isOexPlatform,
    isPagoFacilPlatform,
    isPaytechPlatform,
    isRoundedPlatform,
    isWolfBTCPlatform,
    isXellarPaymentsPlatform,
    logoTitle,
    portalTitle,
    spotTitle,
    traderTitle,
    xplorSpotLiteTitle,
    xplorSpotTitle,
    xplorTraderTitle
} from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectAccountType, selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { showLayoutSwitch } from '@/utils/route';
import cn from 'classnames';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconType } from 'react-icons/lib';
import {
    LuActivity,
    LuArrowDownToLine,
    LuArrowUpFromLine,
    LuBarChart3,
    LuChevronDown,
    LuChevronUp,
    LuLogOut,
    LuPieChart,
    LuRepeat,
    LuSettings,
    LuSliders,
    LuTrendingUp,
    LuZap
} from 'react-icons/lu';
import { MdOutlineAccountBalance, MdOutlineAccountBalanceWallet } from 'react-icons/md';
import { onLogout } from './Navbar';
import { SidebarItem } from './SidebarItem';
import { SidebarPlatforms } from './SidebarPlatforms';

export interface PathProps {
    label: string;
    Icon: IconType;
    path: string;
    permissions: string[];
    onClick?: () => void;
    key?: string;
    tourId?: string;
}

const spot_logo = window.config?.logo;
const lite_logo = window.config?.lite_logo;
const trader_logo = window.config?.trader_logo;

function UniversalSidebar({ settingsDisclosure, isExpanded, setIsExpanded, isAlwaysExpanded }: any) {
    const dispatch = useAppDispatch();

    const pathname = location.pathname.replace(/\/$/, '');

    const lang = useLanguage();
    const accountType = useAppSelector(selectAccountType);
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);

    const currentAccountType = useMemo(() => {
        const properties = credentials?.accounts.find((account) => account.code === currentAccount)?.properties || [];
        return properties.find((property) => property.key === 'TYPE')?.value || 'NOP';
    }, [credentials, currentAccount]);

    const asideProps = useMemo(() => {
        if (isAlwaysExpanded) {
            return {
                // isExpanded: true
            };
        }
        return {
            // isExpanded,
            onMouseEnter: () => setIsExpanded(true),
            onMouseLeave: () => setIsExpanded(false)
        };
    }, [isAlwaysExpanded, isExpanded]);

    const accountItems: PathProps[] = useMemo(() => {
        const items: PathProps[] = [
            {
                Icon: LuSettings,
                key: 'Settings',
                label: lang.navigation['settings'],
                path: '/settings',
                permissions: [],
                tourId: 'security',
                onClick: () => settingsDisclosure[1].toggle()
            },
            {
                Icon: LuLogOut,
                key: 'Logout',
                label: lang.navigation['logout'],
                path: '/logout',
                permissions: [],
                onClick: () => onLogout(credentials, dispatch)
            }
        ];

        return items;
    }, [credentials, pathname, lang, settingsDisclosure, dispatch]);

    const platformItems: PathProps[] = useMemo(() => {
        let platforms = [
            {
                Icon: LuTrendingUp,
                label: xplorSpotTitle,
                path: '/',
                permissions: [],
                key: 'XplorSpot'
            },
            {
                Icon: LuZap,
                label: xplorSpotLiteTitle,
                path: '/lite',
                permissions: [],
                key: 'XplorSpotLite'
            },
            {
                Icon: LuBarChart3,
                label: xplorTraderTitle,
                path: '/trader',
                permissions: [],
                key: 'XplorTrader'
            }
        ];

        if (pathname.includes('portal')) {
            platforms = platforms.filter((p) => p.key !== 'XplorPortal');
            if (currentAccountType === 'NOP') {
                platforms = platforms.filter((p) => p.key !== 'XplorTrader');
            } else if (currentAccountType === 'MARGIN') {
                platforms = platforms.filter((p) => p.key === 'XplorTrader');
            }
        } else if (pathname.includes('trader')) {
            platforms = platforms.filter((p) => p.key === 'XplorTrader');
        } else if (pathname.includes('lite')) {
            platforms = platforms.filter((p) => ['XplorSpot', 'XplorSpotLite'].includes(p.key));
        } else if (pathname === '') {
            platforms = platforms.filter((p) => ['XplorSpotLite', 'XplorSpot'].includes(p.key));
        }

        return platforms;
    }, [currentAccountType, pathname]);

    const portalItems: PathProps[] = useMemo(() => {
        const platforms = [
            {
                key: 'Overview',
                label: lang.navigation['overview'],
                Icon: LuPieChart,
                path: '/portal',
                permissions: ['ACCOUNT_VIEW'],
                tourId: 'account-overview'
            },
            {
                key: 'Deposits',
                label: lang.navigation['deposits'],
                Icon: LuArrowDownToLine,
                path: '/portal/deposits',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_DEPOSIT'],
                tourId: 'account-deposits'
            },
            {
                key: 'Withdrawals',
                label: lang.navigation['withdrawals'],
                Icon: LuArrowUpFromLine,
                path: '/portal/withdrawals',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_WITHDRAWAL'],
                tourId: 'account-withdrawals'
            },
            {
                key: 'Trades',
                label: lang.navigation['trades'],
                Icon: LuActivity,
                path: '/portal/trades',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'Instruments',
                label: lang.navigation['instruments'],
                Icon: LuSliders,
                path: '/portal/instruments',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'FX Swaps',
                label: lang.navigation['fx swaps'],
                Icon: LuRepeat,
                path: '/portal/fx-swaps',
                permissions: ['ACCOUNT_VIEW']
            },
            {
                key: 'Bank Accounts',
                label: lang.navigation['bank accounts'],
                Icon: MdOutlineAccountBalance,
                path: '/portal/bank-accounts',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_WHITELIST'],
                tourId: 'account-bank-accounts'
            },
            {
                key: 'Wallets',
                label: lang.navigation['wallets'],
                Icon: MdOutlineAccountBalanceWallet,
                path: '/portal/wallets',
                permissions: ['ACCOUNT_VIEW', 'REQUEST_WHITELIST'],
                tourId: 'account-wallets'
            }
        ];

        return platforms.filter((platform) => {
            if (accountType === 'MARGIN') return platform.key !== 'Wallets';
            else if (accountType === 'NOP') return platform.key !== 'FX Swaps';
            return true;
        });
    }, [pathname, accountType, lang]);

    const logo_src = useMemo(() => {
        if (pathname.includes('/lite')) return lite_logo;
        else if (pathname.includes('/trader')) return trader_logo;
        else if (pathname.includes('/portal')) return getPlatformLogo();
        else return spot_logo;
    }, [pathname]);

    const collapsed_logo_src = useMemo(() => {
        if (isPaytechPlatform) return '/config/paytech_logo_only.png';
        else if (isArivalPlatform) return '/favicon-arival.ico';
        else if (isWolfBTCPlatform) return '/config/wolfbtc_logo_only.png';
        else if (isGlobalBlockPlatform) return '/config/globalblock_logo_only.png';
        else if (isXellarPaymentsPlatform) return '/favicon-xellar-payments.ico';
        else if (isPagoFacilPlatform) return '/favicon-pago-facil.ico';
        else if (isOexPlatform) return '/favicon-oex.ico';
        else return '/favicon-gcex.ico';
    }, []);

    const logo_alt = useMemo(() => {
        if (pathname.includes('/lite')) return spotTitle;
        else if (pathname.includes('/trader')) return traderTitle;
        else if (pathname.includes('/portal')) return portalTitle;
        else return spotTitle;
    }, [pathname]);

    // Tracks whether the scrollable nav section has more content above/below
    // the visible viewport, so we can render fade + arrow cues.
    const navScrollRef = useRef<HTMLDivElement>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    const updateScrollCues = useCallback(() => {
        const el = navScrollRef.current;
        if (!el) return;
        const hasOverflow = el.scrollHeight > el.clientHeight + 1;
        setCanScrollUp(hasOverflow && el.scrollTop > 1);
        setCanScrollDown(hasOverflow && el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    }, []);

    useEffect(() => {
        updateScrollCues();

        const el = navScrollRef.current;
        if (!el) return;

        const resizeObserver = new ResizeObserver(updateScrollCues);
        resizeObserver.observe(el);

        window.addEventListener('resize', updateScrollCues);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateScrollCues);
        };
        // Re-check whenever nav content or expanded state changes size.
    }, [navScrollRef]);

    return (
        <aside
            {...asideProps}
            data-onboarding="sidebar"
            className={cn(
                `portal-layout hidden lg:flex flex-col fixed left-0 top-0 z-[51] w-full bg-brand-background h-screen transition-all duration-200 ease-in-out border-[3px] border-brand-border text-brand-text`,
                {
                    'max-w-[260px]': isExpanded,
                    'max-w-16': !isExpanded
                }
            )}>
            {/* Logo Section */}
            <div className="relative px-6 md:min-h-[63px] border-b-[3px] border-brand-border flex items-center gap-2">
                <img
                    className={cn(
                        'absolute inset-0 m-auto h-[52px] w-fit cursor-pointer transition-all duration-200 overflow-hidden',
                        {
                            'opacity-100 w-auto': !isExpanded,
                            'opacity-0 w-0': isExpanded,
                            'p-2':
                                isPaytechPlatform ||
                                isArivalPlatform ||
                                isWolfBTCPlatform ||
                                isGlobalBlockPlatform ||
                                isXellarPaymentsPlatform ||
                                isPagoFacilPlatform ||
                                isOexPlatform
                        }
                    )}
                    src={collapsed_logo_src}
                    alt={logo_alt}
                />

                {isRoundedPlatform && (
                    <div className="flex flex-row h-full items-center justify-center gap-3 w-full">
                        <div className="flex flex-col py-2 h-full max-h-12 w-full relative mb-0.5">
                            <img className="block max-h-full max-w-[88.75px]" src={getPlatformLogo()} alt={logoTitle} />
                            {isArivalPlatform && (
                                <div className="text-2xs absolute bottom-0.5 left-8 text-brand-primary-darker">
                                    Powered by GCEX
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {!isRoundedPlatform &&
                    !(
                        isPaytechPlatform ||
                        isWolfBTCPlatform ||
                        isGlobalBlockPlatform ||
                        isXellarPaymentsPlatform ||
                        isPagoFacilPlatform ||
                        isOexPlatform
                    ) && (
                        <img
                            className={cn('block h-10 w-fit transition-all duration-200 overflow-hidden', {
                                'opacity-100 w-auto': isExpanded,
                                'opacity-0 w-0': !isExpanded
                            })}
                            src={logo_src}
                            alt={logo_alt}
                        />
                    )}
                {(isPaytechPlatform ||
                    isWolfBTCPlatform ||
                    isGlobalBlockPlatform ||
                    isXellarPaymentsPlatform ||
                    isOexPlatform) && (
                    <div className="flex flex-row h-full items-center justify-center gap-3 w-full">
                        <div className="flex flex-col h-full max-h-12 py-2 w-full relative">
                            <img
                                className={cn('block w-fit transition-all duration-200 overflow-hidden', {
                                    'opacity-100 w-auto': isExpanded,
                                    'opacity-0 w-0': !isExpanded
                                })}
                                src={logo_src}
                                alt={logo_alt}
                            />
                        </div>
                    </div>
                )}
                {isPagoFacilPlatform && (
                    <div className="flex flex-row h-full items-center justify-center gap-3 w-full">
                        <div className="flex flex-col h-full max-h-12 w-full relative">
                            <img
                                className={cn('block w-fit transition-all duration-200 overflow-hidden', {
                                    'opacity-100 w-auto': isExpanded,
                                    'opacity-0 w-0': !isExpanded
                                })}
                                src={logo_src}
                                alt={logo_alt}
                            />
                        </div>
                    </div>
                )}
            </div>
            {/* Scrollable Navigation Section */}
            <div className="relative flex-1 min-h-0">
                {/* Top scroll cue: fade + arrow shown when content is scrolled down */}
                <div
                    aria-hidden="true"
                    className={cn(
                        'pointer-events-none absolute top-0.5 inset-x-0 flex justify-center z-10 transition-opacity duration-150',
                        canScrollUp ? 'opacity-100' : 'opacity-0'
                    )}>
                    <LuChevronUp className="w-4 h-4 text-brand-text mt-2 animate-bounce" />
                </div>

                <div
                    ref={navScrollRef}
                    onScroll={updateScrollCues}
                    className={cn('h-full overflow-y-auto overflow-x-hidden flex flex-col no-scrollbar', {
                        'p-5 pb-6 px-4 gap-6': isExpanded,
                        'p-2 gap-4': !isExpanded
                    })}>
                    <div>
                        <div className="space-y-1" data-onboarding="other-functions">
                            {/* Actions Group */}
                            {portalItems.map(({ key, ...item }) => (
                                <SidebarItem key={key} isExpanded={isExpanded} {...item} />
                            ))}
                            {/* Platforms Group */}
                            {showLayoutSwitch && platformItems.length > 0 && (
                                <Fragment>
                                    {platformItems.map(({ key, ...item }) => (
                                        <SidebarPlatforms key={key} isExpanded={isExpanded} {...item} />
                                    ))}
                                </Fragment>
                            )}
                        </div>
                    </div>
                </div>

                {/* Bottom scroll cue: fade + arrow shown when more content is below */}
                <div
                    aria-hidden="true"
                    className={cn(
                        'pointer-events-none absolute bottom-0.5 inset-x-0 flex justify-center z-10 transition-opacity duration-150',
                        canScrollDown ? 'opacity-100' : 'opacity-0'
                    )}>
                    <LuChevronDown className="w-4 h-4 text-brand-text/60 animate-bounce" />
                </div>
            </div>

            {/* Account Section (Fixed Bottom) */}
            <div
                className={cn('border-t-[3px] border-brand-border py-3 px-4 flex flex-col gap-1', {
                    'md:min-h-32': !pathname.includes('portal')
                })}>
                {accountItems.map(({ key, ...item }) => (
                    <SidebarItem key={key} isExpanded={isExpanded} {...item} />
                ))}
            </div>
        </aside>
    );
}

export default UniversalSidebar;
